import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Toaster } from '@/components/ui/sonner';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { Layout } from '@/components/common/Layout';
import { ChangePasswordGate } from '@/components/common/ChangePasswordGate';
import { LoginPage } from '@/pages/LoginPage';
import { SetupPasswordPage } from '@/pages/SetupPasswordPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { PublicMapPage } from '@/pages/PublicMapPage';
import { ActionCenterPage } from '@/pages/admin/ActionCenterPage';
import { StockPage } from '@/pages/admin/StockPage';
import { FinancePage } from '@/pages/admin/FinancePage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { PartnersPage } from '@/pages/admin/PartnersPage';
import { ProductsPackagesPage } from '@/pages/admin/ProductsPackagesPage';
import { SettlementPage } from '@/pages/admin/SettlementPage';
import { CreateDOPage } from '@/pages/admin/CreateDOPage';
import { PharmacyMapPage } from '@/pages/admin/PharmacyMapPage';
import { PerformancePage } from '@/pages/admin/PerformancePage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { AdjustmentsPage } from '@/pages/admin/AdjustmentsPage';
import { PartnerDOsPage } from '@/pages/PartnerDOsPage';
import { ReportSalePage } from '@/pages/pharmacy/ReportSalePage';
import { MyStockPage } from '@/pages/pharmacy/MyStockPage';
import { PharmacyInvoicesPage } from '@/pages/pharmacy/InvoicesPage';
import { RestockPage } from '@/pages/pharmacy/RestockPage';
import { PharmacyReturnsPage } from '@/pages/pharmacy/ReturnsPage';
import { IncomingDOsPage } from '@/pages/pharmacy/IncomingDOsPage';
import { LiveSalesPage } from '@/pages/partner/LiveSalesPage';
import { StatementsPage } from '@/pages/partner/StatementsPage';
import { PartnerStockPage } from '@/pages/partner/PartnerStockPage';
import '@/App.css';

const RedirectToRole = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin' || user.role === 'finance' || user.role === 'operations') return <Navigate to="/dashboard" replace />;
  if (user.role === 'pharmacy') return <Navigate to="/pharmacy/report-sale" replace />;
  if (user.role === 'partner') return <Navigate to="/partner/live-sales" replace />;
  return <Navigate to="/login" replace />;
};

const WithLayout = ({ children, roles }) => (
  <ProtectedRoute roles={roles}>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup-password" element={<SetupPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/where-to-buy" element={<PublicMapPage />} />

            <Route path="/dashboard" element={<WithLayout roles={['admin', 'finance', 'operations']}><ActionCenterPage /></WithLayout>} />
            <Route path="/stock" element={<WithLayout roles={['admin', 'operations', 'finance']}><StockPage /></WithLayout>} />
            <Route path="/settlement" element={<WithLayout roles={['admin', 'finance']}><SettlementPage /></WithLayout>} />
            <Route path="/finance" element={<WithLayout roles={['admin', 'finance']}><FinancePage /></WithLayout>} />
            <Route path="/create-do" element={<WithLayout roles={['admin', 'operations']}><CreateDOPage /></WithLayout>} />
            <Route path="/products" element={<WithLayout roles={['admin']}><ProductsPackagesPage /></WithLayout>} />
            <Route path="/pharmacy-map" element={<WithLayout roles={['admin', 'finance']}><PharmacyMapPage /></WithLayout>} />
            <Route path="/performance" element={<WithLayout roles={['admin', 'finance']}><PerformancePage /></WithLayout>} />
            <Route path="/adjustments" element={<WithLayout roles={['admin', 'operations']}><AdjustmentsPage /></WithLayout>} />
            <Route path="/partner-dos" element={<WithLayout roles={['admin', 'operations']}><PartnerDOsPage isAdmin={true} /></WithLayout>} />
            <Route path="/settings" element={<WithLayout roles={['admin']}><SettingsPage /></WithLayout>} />
            <Route path="/users" element={<WithLayout roles={['admin']}><UsersPage /></WithLayout>} />
            <Route path="/partners" element={<WithLayout roles={['admin']}><PartnersPage /></WithLayout>} />

            <Route path="/pharmacy/report-sale" element={<WithLayout roles={['pharmacy']}><ReportSalePage /></WithLayout>} />
            <Route path="/pharmacy/my-stock" element={<WithLayout roles={['pharmacy']}><MyStockPage /></WithLayout>} />
            <Route path="/pharmacy/invoices" element={<WithLayout roles={['pharmacy']}><PharmacyInvoicesPage /></WithLayout>} />
            <Route path="/pharmacy/restock" element={<WithLayout roles={['pharmacy']}><RestockPage /></WithLayout>} />
            <Route path="/pharmacy/returns" element={<WithLayout roles={['pharmacy']}><PharmacyReturnsPage /></WithLayout>} />
            <Route path="/pharmacy/incoming-dos" element={<WithLayout roles={['pharmacy']}><IncomingDOsPage /></WithLayout>} />

            <Route path="/partner/live-sales" element={<WithLayout roles={['partner']}><LiveSalesPage /></WithLayout>} />
            <Route path="/partner/statements" element={<WithLayout roles={['partner']}><StatementsPage /></WithLayout>} />
            <Route path="/partner/where-stock" element={<WithLayout roles={['partner']}><PartnerStockPage /></WithLayout>} />
            <Route path="/partner/direct-dos" element={<WithLayout roles={['partner']}><PartnerDOsPage isAdmin={false} /></WithLayout>} />

            <Route path="/" element={<RedirectToRole />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <ChangePasswordGate />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
